import CommonLocalizations from './common-translations';

type Translation = string;

export type Localizations = {
  [locale: string]: Record<string, Translation>;
}

export type Translator = (key: string | TemplateStringsArray, ...rest: string[]) => string; 


export type LocalizationsProvider = {
  readonly namespace: string;
  readonly localizations: Localizations;
}



export class L10nService {

  private static instance: L10nService;
  readonly i18n_map: Map<string, Localizations>;

  private constructor () {
    this.i18n_map = new Map();
  }

  static isNamespace(namespace: string): boolean {
    return L10nService.getInstance().i18n_map.has(namespace);
  }

  static getTranslator (namespace: string, locale: string): Translator {
    const instance = L10nService.getInstance();
    const localizations = instance.i18n_map.get(namespace);

    if (!localizations) return () => `No localizations for ${namespace}`;
    if (!localizations[locale]) return () => `No locale ${locale} for ${namespace}`;

    return L10nService.toTranslator(locale, localizations);
  }

  static toTranslator (locale: string, localizations: Localizations): Translator {

    const translations = localizations[locale];
    const commonTranslations = CommonLocalizations[locale];

    function l10n(...args: string[]) {
      const [tmpls, ...variables] = args.concat(''); // variables == tmpls.length - 1
      const key = Array.isArray(tmpls)
        ? tmpls.reduce((acc, t, i) => acc + t + variables[i], '')
        : tmpls;
    
      return translations[key] || commonTranslations[key] || `${locale}:${key}`;
    }

    return l10n as Translator;
  }

  static registerLocalizations (namespace: string, localizations: Localizations) {

    const instance = L10nService.getInstance();

    if (instance.i18n_map.has(namespace)) throw new Error(`Duplicate namespace "${namespace}"`);
    
    instance.i18n_map.set(namespace, localizations)
  }

  static getInstance () {
    if (!L10nService.instance) {
      L10nService.instance = new L10nService();
    }
    return L10nService.instance;
  }
}


// Import translations
const modules: Record<string, LocalizationsProvider> = import.meta.glob([
  '/src/theme/**/translations.ts',
],{
  import: 'default',
  eager: true,
})

// ...and register those
Object.values(modules)
  .forEach(x => {
    try {
      const { namespace, localizations } = x;
      L10nService.registerLocalizations(namespace, localizations);
    } catch (error) {
      console.error(error);
    }
  });
